import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import SEO from '../seo/SEO';
import { FiSearch } from 'react-icons/fi';

function Research() {
  const [researchPosts, setResearchPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const postsPerPage = 9;

  const seoData = {
    title: "Research - Intellectual Oasis Fellowship",
    description: "Explore our research initiatives in education, technology, and innovation. Join us in advancing knowledge and transforming education through research.",
    keywords: "education research, educational technology, research initiatives, academic research, Intellectual Oasis Fellowship research",
    image: "/images/research-og.jpg"
  };

  // Static research areas (as these are more categories than dynamic content)
  const researchAreas = [
    {
      title: "Educational Technology",
      description: "Investigating the impact of technology on learning outcomes and developing innovative educational tools.",
      icon: "💻"
    },
    {
      title: "Learning Analytics",
      description: "Analyzing student data to improve educational practices and personalize learning experiences.",
      icon: "📊"
    },
    {
      title: "Curriculum Development",
      description: "Researching and developing effective curriculum frameworks for modern education.",
      icon: "📚"
    },
    {
      title: "Student Success",
      description: "Studying factors that contribute to student achievement and developing support systems.",
      icon: "🎓"
    }
  ];

  // Fetch tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tagQuery = query(collection(db, "research_tags"), orderBy("name"));
        const tagSnapshot = await getDocs(tagQuery);
        const tagList = tagSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTags(tagList);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    fetchTags();
  }, []);

  // Fetch research posts
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        let postsQuery;
        
        if (selectedTag === 'all') {
          postsQuery = query(
            collection(db, "research_posts"),
            orderBy("publishedDate", "desc"),
            limit(postsPerPage)
          );
        } else {
          postsQuery = query(
            collection(db, "research_posts"),
            where("tags", "array-contains", selectedTag),
            orderBy("publishedDate", "desc"),
            limit(postsPerPage)
          );
        }
        
        const postSnapshot = await getDocs(postsQuery);
        const postList = postSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setResearchPosts(postList);
        setLastVisible(postSnapshot.docs[postSnapshot.docs.length - 1]);
        setHasMore(postSnapshot.docs.length === postsPerPage);
      } catch (error) {
        console.error("Error fetching research posts:", error);
      }
      setLoading(false);
    };

    fetchPosts();
  }, [selectedTag]);

  // Load more posts
  const loadMorePosts = async () => {
    if (!lastVisible) return;
    
    setLoading(true);
    try {
      let postsQuery;
      
      if (selectedTag === 'all') {
        postsQuery = query(
          collection(db, "research_posts"),
          orderBy("publishedDate", "desc"),
          startAfter(lastVisible),
          limit(postsPerPage)
        );
      } else {
        postsQuery = query(
          collection(db, "research_posts"),
          where("tags", "array-contains", selectedTag),
          orderBy("publishedDate", "desc"),
          startAfter(lastVisible),
          limit(postsPerPage)
        );
      }
      
      const postSnapshot = await getDocs(postsQuery);
      const newPosts = postSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setResearchPosts(prev => [...prev, ...newPosts]);
      setLastVisible(postSnapshot.docs[postSnapshot.docs.length - 1] || null);
      setHasMore(postSnapshot.docs.length === postsPerPage);
    } catch (error) {
      console.error("Error loading more posts:", error);
    }
    setLoading(false);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Filter posts based on search query and selected tag
  const getFilteredPosts = () => {
    return researchPosts.filter(post => {
      const matchesSearch = searchQuery === '' || 
        post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.abstract?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.authors?.some(author => author.toLowerCase().includes(searchQuery.toLowerCase())) ||
        post.institution?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTag = selectedTag === 'all' || post.tags?.includes(selectedTag);
      
      return matchesSearch && matchesTag;
    });
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-2xl font-bold text-iof mb-4 sm:mb-6">Research at Intellectual Oasis Fellowship</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advancing education through innovative research and evidence-based practices
          </p>
        </div>

        {/* Research Areas */}
        <div className="mb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-iof mb-4 sm:mb-6">Research Areas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {researchAreas.map((area, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="text-4xl mb-4">{area.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{area.title}</h3>
                <p className="text-gray-600">{area.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Research Publications */}
        <div className="mb-16">
          <div className="mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-iof mb-4 sm:mb-6">Research Publications</h2>
            {/* Search and Filter - horizontal single pane with scrollable tags */}
            <div className="flex gap-4 w-full md:w-[700px] mx-auto items-center overflow-x-auto pb-2">
              <div className="relative min-w-[220px] flex-shrink-0">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search publications..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[rgb(130,88,18)] focus:border-transparent"
                />
              </div>
              {/* Tags as horizontal scrollable pills/buttons */}
              
            </div>
          </div>

          {/* Publications Grid */}
          {loading && researchPosts.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
            </div>
          ) : getFilteredPosts().length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {getFilteredPosts().map((post) => (
                  <div 
                    key={post.id} 
                    className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => setSelectedPost(post)}
                  >
                    <h3 className="text-xl font-semibold mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-sm text-gray-500 mb-2">
                      {post.authors?.join(', ') || post.institution || 'Unknown author'}
                    </p>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.abstract}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags?.map((tagId, index) => {
                        const tagObj = tags.find(t => t.id === tagId);
                        return tagObj ? (
                          <span 
                            key={index} 
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                          >
                            {tagObj.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(post.publishedDate)}
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination Controls: Jump to page only */}
              <div className="flex justify-center mt-8">
                <div className="flex items-center gap-2">
                  <span>Jump to page:</span>
                  <input
                    type="number"
                    min={1}
                    max={10} // TODO: replace with totalPages
                    className="w-16 px-2 py-1 border rounded"
                    value={1} // TODO: bind to jumpPage state
                    disabled
                  />
                  <button className="px-3 py-1 rounded bg-[rgb(130,88,18)] text-white" disabled>Go</button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">No research publications found.</p>
            </div>
          )}
        </div>

        {/* Research Post Modal */}
        {selectedPost && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPost.title}</h2>
                  <button 
                    onClick={() => setSelectedPost(null)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Close modal"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 mb-4">
                  By {selectedPost.authors?.join(', ') || 'Unknown author'}
                  {selectedPost.institution && ` - ${selectedPost.institution}`}
                </p>
                
                <p className="text-sm text-gray-500 mb-6">
                  Published: {formatDate(selectedPost.publishedDate)}
                </p>
                
                <div className="prose max-w-none mb-6">
                  <h3 className="text-lg font-semibold mb-2">Abstract</h3>
                  <p>{selectedPost.abstract}</p>
                </div>
                
                {selectedPost.externalLink && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">External Link</h3>
                    <a 
                      href={selectedPost.externalLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View full research
                    </a>
                  </div>
                )}
                
                {selectedPost.researchGateProfile && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">ResearchGate Profile</h3>
                    <a 
                      href={selectedPost.researchGateProfile} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      <span className="mr-2">View researcher's profile</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedPost.tags?.map((tagId, index) => {
                    const tagObj = tags.find(t => t.id === tagId);
                    return tagObj ? (
                      <span 
                        key={index} 
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tagObj.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center bg-[rgb(130,88,18)] text-white p-8 rounded-lg">
          <h2 className="text-xl sm:text-2xl font-bold text-iof mb-4 sm:mb-6">Join Our Research Team</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            We're always looking for passionate researchers to join our team. Contribute to meaningful research that shapes the future of education.
          </p>
          <Link
            to="/careers"
            className="bg-white text-[rgb(130,88,18)] px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors duration-300"
          >
            View Open Positions
          </Link>
        </div>
      </div>
    </>
  );
}

export default Research; 